import { useMemo, useState, type FormEvent } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useCreateSpec, useUpdateSpec } from "@/api/mutations/provisioning";
import { useCells, useReleases, useSpecDefaults } from "@/api/queries/provisioning";
import { useCustomers } from "@/api/queries/customers";
import { useLicenses } from "@/api/queries/licenses";
import type { CustomerSpec, CustomerSpecWithInvite, ReleaseChannel, SpecChannel } from "@/types";

// Kanaele (#341): die Spezifikation traegt Name, Typ, Anzeigename und fuer WhatsApp optional die Pairing-Nummer —
// keine Geheimnisse. Tokens (Slack/Teams) bzw. den QR-Code hinterlegt der Kunde spaeter in SEINER Admin-Konsole.
// Review M6 (#346): `env` reist unsichtbar mit — das Formular ist ein Voll-Ersatz, ein Bestand (Pairing-Nummer, LOG_LEVEL) darf
// beim blossen Speichern nicht verschwinden. Gesetzt wird es hier nicht (Kopplung passiert beim Kunden).
type ChannelRow = { name: string; type: SpecChannel["type"]; displayName: string; env?: Record<string, string> };
const CHANNEL_TYPES: Array<{ value: SpecChannel["type"]; label: string }> = [
  { value: "whatsapp", label: "WhatsApp" },
  { value: "slack", label: "Slack" },
  { value: "teams", label: "Microsoft Teams" },
];
function rowsFrom(channels: SpecChannel[] | undefined): ChannelRow[] {
  return (channels ?? []).map((c) => ({ name: c.name, type: c.type, displayName: c.displayName ?? "", ...(c.env ? { env: c.env } : {}) }));
}
function channelsFrom(rows: ChannelRow[]): SpecChannel[] {
  return rows.map((r) => ({
    name: r.name.trim(),
    type: r.type,
    ...(r.displayName.trim() ? { displayName: r.displayName.trim() } : {}),
    ...(r.env && Object.keys(r.env).length > 0 ? { env: r.env } : {}),
  }));
}

const SELECT_CLASS = "flex h-10 w-full rounded-md border border-border bg-base px-3 py-2 text-sm text-text";

// #343 UC-2/UC-8: Kundenspezifikation anlegen bzw. aendern. EIN Formular fuer beide Faelle — beim Aendern sind Kunde,
// Zelle und Kundenname unveraenderlich (Umzug = Backup/Restore). `values` als JSON (nur Allowlist-Schluessel — das
// Portal prueft, der Agent ist der Pruefer). Geheimnisse (Lizenz → Aktivierungsschluessel, Registry-Token) gibt es
// nur beim Anlegen; danach ueber die eigenen Aktionen der Detailseite.
// #343 (UC-9): `onCreated` bekommt die Antwort des Anlegens — sie trägt die Einladung des Erst-Admins, die der
// Aufrufer sofort zeigt (der Dialog wird beim Schliessen abgebaut, darum lebt der Link-Dialog beim Aufrufer).
export function SpecDialog({ open, onOpenChange, existing, presetCustomerId, presetCellId, onCreated }: { open: boolean; onOpenChange: (open: boolean) => void; existing?: CustomerSpec; presetCustomerId?: string; presetCellId?: string; onCreated?: (created: CustomerSpecWithInvite) => void }) {
  const customers = useCustomers();
  const cells = useCells();
  const releases = useReleases();
  const create = useCreateSpec();
  const update = useUpdateSpec();
  // Kennung + Standard-Host kommen aus dem Kunden (`elys-000001` → `elys-000001.<domain>`), Arben 09.09.
  const defaults = useSpecDefaults();

  const [customerId, setCustomerId] = useState(existing?.customerId ?? presetCustomerId ?? "");
  const [cellId, setCellId] = useState(existing?.cellId ?? presetCellId ?? "");
  const [customer, setCustomer] = useState(existing?.customer ?? "");
  const [plan, setPlan] = useState(existing?.plan ?? "s");
  const [channel, setChannel] = useState<ReleaseChannel>(existing?.updates.channel ?? "stable");
  const [mode, setMode] = useState(existing?.updates.mode ?? "manual");
  const [window, setWindow] = useState(existing?.updates.window ?? "");
  const [imageTag, setImageTag] = useState(existing?.imageTag ?? "");
  const [host, setHost] = useState(existing?.host ?? "");
  const [relay, setRelay] = useState(existing?.features.relay ?? false);
  // #346 UC-1: eigenes LLM-Gateway (LiteLLM + Postgres im Kunden-Namespace); Entscheid Arben 09.09.: DB je Kunde.
  const [litellm, setLitellm] = useState(existing?.features.litellm ?? false);
  const [adminEmail, setAdminEmail] = useState(existing?.adminEmail ?? "");
  const [channelRows, setChannelRows] = useState<ChannelRow[]>(rowsFrom(existing?.channels));
  const [valuesText, setValuesText] = useState(existing ? JSON.stringify(existing.values, null, 2) : "{}");
  const [licenseId, setLicenseId] = useState("");
  const [registryUser, setRegistryUser] = useState("");
  const [registryToken, setRegistryToken] = useState("");
  const [localError, setLocalError] = useState<string | null>(null);

  const licenses = useLicenses(customerId || undefined);
  const selectedCustomer = (customers.data ?? []).find((c) => c.id === customerId);
  const suggestedCode = selectedCustomer?.code ?? "";
  const suggestedHost = suggestedCode && defaults.data?.customerDomain ? `${suggestedCode}.${defaults.data.customerDomain}` : "";
  // Beim Anlegen ist die Kennung des Kunden der Namespace — Freitext nur, wenn der Kunde (aelteres Portal) keine hat.
  const effectiveCustomer = existing ? existing.customer : suggestedCode || customer.trim();
  const availableReleases = useMemo(() => (releases.data ?? []).filter((r) => channel === "beta" || r.channel === "stable"), [releases.data, channel]);
  const enrolledCells = (cells.data ?? []).filter((c) => c.status === "enrolled" || c.id === cellId);
  const pending = create.isPending || update.isPending;
  const error = localError ?? (create.isError ? create.error.message : update.isError ? update.error.message : null);

  function submit(e: FormEvent) {
    e.preventDefault();
    setLocalError(null);
    let values: Record<string, unknown>;
    try {
      values = valuesText.trim() ? (JSON.parse(valuesText) as Record<string, unknown>) : {};
    } catch {
      setLocalError("values: kein gueltiges JSON");
      return;
    }
    const updates = { mode, channel, window: window.trim() || null };
    if (channelRows.some((r) => !/^[a-z0-9]([a-z0-9-]{0,30}[a-z0-9])?$/.test(r.name.trim()))) {
      setLocalError("Kanal-Name: Kleinbuchstaben, Ziffern, Bindestrich (max 32), z.B. wa1");
      return;
    }
    const channels = channelsFrom(channelRows);
    if (existing) {
      update.mutate(
        { id: existing.id, patch: { plan, imageTag, host: host.trim() || null, features: { relay, litellm }, values, updates, adminEmail: adminEmail.trim() || null, channels } },
        { onSuccess: () => onOpenChange(false) },
      );
      return;
    }
    create.mutate(
      {
        customerId,
        cellId,
        customer: effectiveCustomer,
        plan,
        imageTag,
        // leer = das Portal nimmt `<kennung>.<domain>`; ein Wert ueberschreibt.
        host: host.trim() || undefined,
        features: { relay, litellm },
        values,
        updates,
        adminEmail: adminEmail.trim() || null,
        channels,
        licenseId: licenseId || null,
        registry: registryUser.trim() && registryToken ? { username: registryUser.trim(), token: registryToken } : null,
      },
      { onSuccess: (created) => { onOpenChange(false); onCreated?.(created); } },
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="spec-dialog" className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{existing ? `Spezifikation aendern (${existing.customer}@${existing.cell})` : "Kundenspezifikation anlegen"}</DialogTitle>
          <DialogDescription>
            {existing ? "Jede Aenderung ist eine neue Version; der Agent der Zelle rollt sie aus (Versionswechsel im Wartungsfenster)." : "Das Portal ist die Quelle des Soll-Zustands; der Agent der Zelle holt den signierten Auftrag ab und rollt den Kunden aus."}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          {!existing && (
            <>
              <div className="space-y-2">
                <Label htmlFor="spec-customer-id">Kunde (Portal)</Label>
                <select id="spec-customer-id" value={customerId} onChange={(e) => setCustomerId(e.target.value)} required disabled={!!presetCustomerId} className={SELECT_CLASS} data-testid="spec-customer-id">
                  <option value="" disabled>— Kunde waehlen —</option>
                  {(customers.data ?? []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="spec-cell">Zelle (enrollt)</Label>
                <select id="spec-cell" value={cellId} onChange={(e) => setCellId(e.target.value)} required disabled={!!presetCellId} className={SELECT_CLASS} data-testid="spec-cell">
                  <option value="" disabled>— Zelle waehlen —</option>
                  {enrolledCells.map((c) => <option key={c.id} value={c.id}>{c.name} ({c.provider})</option>)}
                </select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="spec-customer">Kennung in der Zelle (Namespace)</Label>
                {suggestedCode ? (
                  <Input id="spec-customer" value={suggestedCode} readOnly className="mono" data-testid="spec-customer" />
                ) : (
                  <Input id="spec-customer" value={customer} onChange={(e) => setCustomer(e.target.value)} required placeholder="acme" data-testid="spec-customer" />
                )}
                <p className="text-xs text-text3">{suggestedCode ? "Aus dem Kunden vorgegeben — fortlaufend, unveraenderlich." : "Kunde ohne Kennung: Namespace-Name von Hand (DNS-Label)."}</p>
              </div>
            </>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="spec-plan">Plan</Label>
              <select id="spec-plan" value={plan} onChange={(e) => setPlan(e.target.value)} className={SELECT_CLASS} data-testid="spec-plan">
                {["s", "m", "l"].map((p) => <option key={p} value={p}>{p.toUpperCase()}</option>)}
              </select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="spec-channel">Kanal</Label>
              <select id="spec-channel" value={channel} onChange={(e) => setChannel(e.target.value as ReleaseChannel)} className={SELECT_CLASS} data-testid="spec-channel">
                <option value="stable">stable</option>
                <option value="beta">beta</option>
              </select>
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="spec-image">Version (Release-Tag)</Label>
            <select id="spec-image" value={imageTag} onChange={(e) => setImageTag(e.target.value)} required className={SELECT_CLASS} data-testid="spec-image">
              <option value="" disabled>— Release waehlen —</option>
              {availableReleases.map((r) => <option key={r.version} value={r.version}>{r.version} ({r.channel})</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="spec-mode">Update-Modus</Label>
              <select id="spec-mode" value={mode} onChange={(e) => setMode(e.target.value as "manual" | "auto")} className={SELECT_CLASS} data-testid="spec-mode">
                <option value="manual">manual</option>
                <option value="auto">auto</option>
              </select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="spec-window">Wartungsfenster</Label>
              <Input id="spec-window" value={window} onChange={(e) => setWindow(e.target.value)} placeholder="sat,sun 02:00-05:00 Europe/Zurich" data-testid="spec-window" />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="spec-host">Host (Ingress + TLS)</Label>
            <Input id="spec-host" value={host} onChange={(e) => setHost(e.target.value)} placeholder={suggestedHost || "elys-000001.elyvers.com"} data-testid="spec-host" />
            {!existing && suggestedHost && !host.trim() && <p className="text-xs text-text3" data-testid="spec-host-hint">Leer = {suggestedHost}</p>}
          </div>
          <div className="space-y-2">
            <Label htmlFor="spec-admin">Bootstrap-Admin (E-Mail)</Label>
            <Input id="spec-admin" type="email" value={adminEmail} onChange={(e) => setAdminEmail(e.target.value)} placeholder="it@acme.ch" data-testid="spec-admin" />
          </div>
          <label className="flex items-center gap-2 text-sm text-text2">
            <input type="checkbox" checked={relay} onChange={(e) => setRelay(e.target.checked)} data-testid="spec-relay" />
            eigener Syncthing-Relay
          </label>
          <div className="space-y-2" data-testid="spec-channels">
            <div className="flex items-center justify-between">
              <Label>Kanaele (WhatsApp, Slack, Teams)</Label>
              <Button type="button" size="sm" variant="outline" onClick={() => setChannelRows((rows) => [...rows, { name: `ch${rows.length + 1}`, type: "whatsapp", displayName: "" }])} data-testid="spec-channel-add">
                + Kanal
              </Button>
            </div>
            {channelRows.length === 0 ? (
              <p className="text-xs text-text3">Keine Kanaele — der Kunde erreicht seine Agenten ueber die Lior-App; Kanaele lassen sich spaeter als neue Version ergaenzen.</p>
            ) : (
              channelRows.map((row, i) => (
                <div key={i} className="grid grid-cols-[1fr_1fr_1fr_auto] items-end gap-2 rounded-md border border-border p-2" data-testid={`spec-channel-row-${i}`}>
                  <div className="space-y-1">
                    <Label htmlFor={`spec-channel-name-${i}`} className="text-xs">Name (technisch)</Label>
                    <Input id={`spec-channel-name-${i}`} value={row.name} onChange={(e) => setChannelRows((rows) => rows.map((r, j) => (j === i ? { ...r, name: e.target.value } : r)))} placeholder="wa1" className="font-mono" data-testid={`spec-channel-name-${i}`} />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor={`spec-channel-type-${i}`} className="text-xs">Typ</Label>
                    <select id={`spec-channel-type-${i}`} value={row.type} onChange={(e) => setChannelRows((rows) => rows.map((r, j) => (j === i ? { ...r, type: e.target.value as SpecChannel["type"] } : r)))} className={SELECT_CLASS} data-testid={`spec-channel-type-${i}`}>
                      {CHANNEL_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor={`spec-channel-display-${i}`} className="text-xs">Anzeigename (optional)</Label>
                    <Input id={`spec-channel-display-${i}`} value={row.displayName} onChange={(e) => setChannelRows((rows) => rows.map((r, j) => (j === i ? { ...r, displayName: e.target.value } : r)))} placeholder={row.type === "whatsapp" ? "WhatsApp Buero" : "Team Slack"} data-testid={`spec-channel-display-${i}`} />
                  </div>
                  <Button type="button" size="sm" variant="ghost" onClick={() => setChannelRows((rows) => rows.filter((_, j) => j !== i))} data-testid={`spec-channel-remove-${i}`}>entfernen</Button>
                </div>
              ))
            )}
            {channelRows.length > 0 && <p className="text-xs text-text3">Die Zelle legt je Kanal einen Container an. Koppeln (WhatsApp-QR) und Zugangsdaten (Slack-/Teams-Tokens) macht der Kunde danach in seiner Admin-Konsole unter «Channels».</p>}
          </div>
          <div className="space-y-1" data-testid="spec-addons">
            <Label>Zusatzbausteine</Label>
            <label className="flex items-center gap-2 text-sm text-text2">
              <input type="checkbox" checked={litellm} onChange={(e) => setLitellm(e.target.checked)} data-testid="spec-litellm" />
              eigenes LLM-Gateway (LiteLLM + Datenbank im Kunden-Namespace — andere Anbieter als Anthropic anbinden)
            </label>
            {existing?.features.litellm && !litellm && (
              <p className="text-xs text-warn" data-testid="spec-litellm-warn">
                Abschalten baut Gateway und Datenbank ab. Agenten oder Prozesse, die noch einen Gateway-Slot nutzen, scheitern danach
                mit «LLM nicht konfiguriert» — vorher in der Admin-Konsole des Kunden umhängen. Die Datenbank-Daten bleiben erhalten.
              </p>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="spec-values">Chart-Werte (JSON, nur Allowlist-Schluessel)</Label>
            <Textarea id="spec-values" value={valuesText} onChange={(e) => setValuesText(e.target.value)} rows={5} className="font-mono text-xs" data-testid="spec-values" />
          </div>
          {!existing && (
            <>
              <div className="space-y-2">
                <Label htmlFor="spec-license">Lizenz (Aktivierungsschluessel wird versiegelt an die Zelle)</Label>
                <select id="spec-license" value={licenseId} onChange={(e) => setLicenseId(e.target.value)} className={SELECT_CLASS} data-testid="spec-license">
                  <option value="">— ohne Lizenz —</option>
                  {(licenses.data ?? []).filter((l) => l.status === "active").map((l) => <option key={l.id} value={l.id}>{l.edition} · bis {l.validUntil.slice(0, 10)}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="spec-reg-user">Registry-Nutzer</Label>
                  <Input id="spec-reg-user" value={registryUser} onChange={(e) => setRegistryUser(e.target.value)} data-testid="spec-registry-user" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="spec-reg-token">Registry-Token (write-only)</Label>
                  <Input id="spec-reg-token" type="password" value={registryToken} onChange={(e) => setRegistryToken(e.target.value)} data-testid="spec-registry-token" />
                </div>
              </div>
            </>
          )}
          {error && <div className="text-sm text-danger" data-testid="spec-error">{error}</div>}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={pending}>Abbrechen</Button>
            <Button type="submit" disabled={pending} data-testid="spec-submit">{pending ? "…" : existing ? "Neue Version" : "Anlegen"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
